import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MagicLineIcon from 'remixicon-react/MagicLineIcon';

interface AiFeedbackProps {
    message?: string;
    disclaimer: string;
    isLoading?: boolean;
    onExampleClick?: (example: string) => void;
    className?: string;
}

interface MessagePart {
    text: string;
    isExample: boolean;
}

const QUOTED_TEXT = /["“”«»]([^"“”«»]+)["“”«»]/g;

const splitExamples = (message: string): MessagePart[] => {
    const parts: MessagePart[] = [];
    let lastIndex = 0;

    for (const match of message.matchAll(QUOTED_TEXT)) {
        const start = match.index ?? 0;

        if (start > lastIndex)
            parts.push({ text: message.slice(lastIndex, start), isExample: false });

        parts.push({ text: match[1], isExample: true });
        lastIndex = start + match[0].length;
    }

    if (lastIndex < message.length)
        parts.push({ text: message.slice(lastIndex), isExample: false });

    return parts;
};

// scrollHeight and clientHeight round fractional line heights differently.
const isClamped = (element: HTMLElement | null) =>
    !!element && element.scrollHeight - element.clientHeight > 1;

const AiFeedback: React.FC<AiFeedbackProps> = ({
    message,
    disclaimer,
    isLoading = false,
    onExampleClick,
    className,
}: AiFeedbackProps) => {
    const messageRef = useRef<HTMLParagraphElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(false);

    useEffect(() => setExpanded(false), [message]);

    useLayoutEffect(() => {
        if (expanded) return;
        setOverflowing(isClamped(messageRef.current));
    }, [message, isLoading, expanded]);

    const renderPart = (part: MessagePart, index: number) =>
        part.isExample && onExampleClick ? (
            <ExampleLink
                key={index}
                role="button"
                tabIndex={0}
                onClick={() => onExampleClick(part.text)}
                onKeyDown={e => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    onExampleClick(part.text);
                }}
                title="Search for this"
                data-testid="ai-feedback-example"
            >
                {part.text}
            </ExampleLink>
        ) : (
            <React.Fragment key={index}>
                {part.isExample ? `"${part.text}"` : part.text}
            </React.Fragment>
        );

    return (
        <Container className={className} data-testid="ai-feedback">
            <div aria-live="polite">
                {isLoading && (
                    <MessageRow>
                        <Interpreting data-testid="ai-feedback-loading">Interpreting…</Interpreting>
                    </MessageRow>
                )}
                {!isLoading && message && (
                    <MessageRow>
                        <MagicLineIcon size={12} />
                        <Message
                            ref={messageRef}
                            $expanded={expanded}
                            data-testid="ai-feedback-message"
                        >
                            <span>{splitExamples(message).map(renderPart)}</span>
                        </Message>
                    </MessageRow>
                )}
                {!isLoading && message && overflowing && (
                    <ToggleButton
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                        data-testid="ai-feedback-toggle"
                    >
                        {expanded ? 'Show less' : 'Show more'}
                    </ToggleButton>
                )}
            </div>
            <Disclaimer data-testid="ai-feedback-disclaimer">{disclaimer}</Disclaimer>
        </Container>
    );
};

const Container = styled.div`
    width: 50rem;
    font-size: ${props => props.theme.text.size.small};
`;

const MessageRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    min-height: 1.6rem;
    color: ${props => props.theme.colors.text.primary};

    svg {
        flex-shrink: 0;
        margin-top: 0.2rem;
    }
`;

const Message = styled.p<{ $expanded: boolean }>`
    flex: 1;
    min-width: 0;
    margin: 0;
    display: ${props => (props.$expanded ? 'block' : '-webkit-box')};
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const ToggleButton = styled.button`
    margin-left: 1.6rem;
    padding: 0;
    border: none;
    background: none;
    color: ${props => props.theme.colors.text.secondary};
    font-size: inherit;
    text-decoration: underline;
    cursor: pointer;
`;

const Interpreting = styled.span`
    margin-left: 1.6rem;
    color: ${props => props.theme.colors.text.secondary};
`;

const ExampleLink = styled.span`
    text-decoration: underline dotted;
    text-underline-offset: 0.2rem;
    cursor: pointer;

    &:hover {
        text-decoration-style: solid;
    }

    &:focus-visible {
        outline: 0.1rem solid ${props => props.theme.colors.primary};
        outline-offset: 0.1rem;
    }
`;

const Disclaimer = styled.p`
    margin: 0.3rem 0 0 1.6rem;
    color: ${props => props.theme.colors.text.secondary};
    font-style: italic;
`;

export default AiFeedback;
